import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { useMyRestaurants } from '../hooks/useMyRestaurants';
import { formatMoney, parseMoneyToCents } from '../lib/format';

interface Trends { level: string; restaurants: number; avg_ticket_cents?: number; avg_turnover_min?: number }
interface Bonif { id: string; name: string; kind: string; value: number; is_active: boolean }

export function SettingsPage() {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { restaurants, loading } = useMyRestaurants();
  const restaurant = restaurants[0];

  const [share, setShare] = useState(false);
  const [trends, setTrends] = useState<Trends | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  // pagamento
  const [pixKey, setPixKey] = useState('');
  const [pixName, setPixName] = useState('');
  const [pixCity, setPixCity] = useState('');
  const [sepaIban, setSepaIban] = useState('');
  const [sepaName, setSepaName] = useState('');
  const [paySaved, setPaySaved] = useState(false);

  // taxa de serviço & couvert
  const [servicePct, setServicePct] = useState('10');
  const [serviceDefault, setServiceDefault] = useState(false);
  const [couvert, setCouvert] = useState('0');
  const [chargesSaved, setChargesSaved] = useState(false);

  // bonificações
  const [bonifs, setBonifs] = useState<Bonif[]>([]);
  const [bName, setBName] = useState('');
  const [bKind, setBKind] = useState('discount_value');
  const [bValue, setBValue] = useState('');

  const load = useCallback(async () => {
    if (!restaurant) return;
    const { data } = await supabase
      .from('restaurants')
      .select('share_data, cuisine_category, region, service_charge_pct, service_charge_default, couvert_value_cents')
      .eq('id', restaurant.id)
      .single();
    if (data) {
      setShare(data.share_data);
      setServicePct(String(data.service_charge_pct ?? 10));
      setServiceDefault(Boolean(data.service_charge_default));
      setCouvert(((data.couvert_value_cents ?? 0) / 100).toString());
      if (data.share_data && data.cuisine_category) {
        const { data: tr } = await supabase.rpc('market_trends', { p_category: data.cuisine_category, p_region: data.region });
        if (tr) setTrends(tr as Trends);
      }
    }
    const { data: pay } = await supabase.from('restaurant_payment').select('*').eq('restaurant_id', restaurant.id).maybeSingle();
    if (pay) {
      setPixKey(pay.pix_key ?? '');
      setPixName(pay.pix_merchant_name ?? '');
      setPixCity(pay.pix_city ?? '');
      setSepaIban(pay.sepa_iban ?? '');
      setSepaName(pay.sepa_name ?? '');
    }
    const { data: b } = await supabase.from('bonifications').select('id, name, kind, value, is_active').eq('restaurant_id', restaurant.id).order('created_at');
    if (b) setBonifs(b as Bonif[]);
  }, [restaurant]);

  useEffect(() => { void load(); }, [load]);

  async function toggleShare(value: boolean) {
    if (!restaurant) return;
    setShare(value);
    await supabase.from('restaurants').update({ share_data: value }).eq('id', restaurant.id);
    void load();
  }

  async function savePayment(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!restaurant) return;
    setPaySaved(false);
    await supabase.from('restaurant_payment').upsert({
      restaurant_id: restaurant.id,
      pix_key: pixKey || null, pix_merchant_name: pixName || null, pix_city: pixCity || null,
      sepa_iban: sepaIban || null, sepa_name: sepaName || null
    }, { onConflict: 'restaurant_id' });
    setPaySaved(true);
  }

  async function saveCharges(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!restaurant) return;
    setChargesSaved(false);
    await supabase.from('restaurants').update({
      service_charge_pct: Number(servicePct) || 0,
      service_charge_default: serviceDefault,
      couvert_value_cents: parseMoneyToCents(couvert)
    }).eq('id', restaurant.id);
    setChargesSaved(true);
  }

  async function addBonif(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!restaurant) return;
    const value = bKind === 'discount_value' ? parseMoneyToCents(bValue) : Number(bValue) || 0;
    await supabase.from('bonifications').insert({ restaurant_id: restaurant.id, name: bName.trim(), kind: bKind, value });
    setBName(''); setBValue('');
    void load();
  }
  async function toggleBonif(b: Bonif) {
    await supabase.from('bonifications').update({ is_active: !b.is_active }).eq('id', b.id);
    void load();
  }
  async function delBonif(id: string) {
    await supabase.from('bonifications').delete().eq('id', id);
    void load();
  }

  async function exportData() {
    const { data } = await supabase.rpc('export_my_data');
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'meus-dados-lapetitetable.json';
    a.click();
  }
  async function deleteData() {
    if (!window.confirm(t('settings.confirm_delete'))) return;
    await supabase.rpc('delete_my_data');
    setMsg(t('settings.deleted'));
    await signOut();
    navigate('/');
  }

  if (loading || !restaurant) return <div className="center-note">{t('common.loading')}</div>;
  const currency = restaurant.currency;
  const locale = restaurant.locale;
  const bonifLabel = (b: Bonif) => (b.kind === 'discount_value' ? formatMoney(b.value, currency, locale) : `${b.value}%`);

  return (
    <div className="page">
      <h1 className="page__title">{t('settings.title')}</h1>

      {/* Taxa de serviço & couvert */}
      <h2 className="section-title">{t('settings.charges_title')}</h2>
      <form onSubmit={saveCharges} className="form">
        <div className="form-row">
          <label className="field">
            <span>{t('settings.service_pct')}</span>
            <input type="number" min={0} step="0.5" value={servicePct} onChange={(e) => setServicePct(e.target.value)} />
          </label>
          <label className="field">
            <span>{t('settings.couvert_value')} ({currency})</span>
            <input value={couvert} onChange={(e) => setCouvert(e.target.value)} inputMode="decimal" />
          </label>
        </div>
        <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <input type="checkbox" checked={serviceDefault} onChange={(e) => setServiceDefault(e.target.checked)} />
          <span>{t('settings.service_default')}</span>
        </label>
        <div className="form-row">
          <button type="submit" className="btn btn--primary">{t('common.save')}</button>
          {chargesSaved && <span className="form-info">{t('loyalty.saved')}</span>}
        </div>
      </form>

      {/* Bonificações */}
      <h2 className="section-title">{t('settings.bonif_title')}</h2>
      <p className="muted-note" style={{ marginTop: -12, marginBottom: 8 }}>{t('settings.bonif_hint')}</p>
      {bonifs.length === 0 ? (
        <p className="muted-note">{t('settings.bonif_none')}</p>
      ) : (
        <ul className="list">
          {bonifs.map((b) => (
            <li key={b.id} className="list__row">
              <span className={b.is_active ? '' : 'dimmed'}>{b.name} · {bonifLabel(b)}</span>
              <span className="row-actions">
                <button type="button" className="link-button" onClick={() => void toggleBonif(b)}>{b.is_active ? '✓' : '—'}</button>
                <button type="button" className="link-button" onClick={() => void delBonif(b.id)}>{t('menu.delete')}</button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={addBonif} className="form inline-form">
        <div className="form-row">
          <label className="field" style={{ flex: 2 }}>
            <span>{t('settings.bonif_name')}</span>
            <input required value={bName} onChange={(e) => setBName(e.target.value)} />
          </label>
          <label className="field">
            <span>{t('settings.bonif_kind')}</span>
            <select value={bKind} onChange={(e) => setBKind(e.target.value)}>
              <option value="discount_value">{t('settings.bonif_value')}</option>
              <option value="discount_pct">{t('settings.bonif_pct')}</option>
            </select>
          </label>
          <label className="field">
            <span>{bKind === 'discount_value' ? currency : '%'}</span>
            <input required value={bValue} onChange={(e) => setBValue(e.target.value)} inputMode="decimal" />
          </label>
        </div>
        <button type="submit" className="btn btn--ghost btn--sm">{t('settings.bonif_add')}</button>
      </form>

      {/* Portal de tendências */}
      <h2 className="section-title">{t('settings.share_title')}</h2>
      <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <input type="checkbox" checked={share} onChange={(e) => void toggleShare(e.target.checked)} />
        <span>{t('settings.share_label')}</span>
      </label>
      <p className="muted-note">{t('settings.share_hint')}</p>
      {share && (!trends || trends.level === 'insufficient' ? (
        <p className="muted-note">{t('settings.trends_insufficient')}</p>
      ) : (
        <div className="stat-grid">
          <div className="stat"><span className="stat__label">{t('settings.level')}</span><span className="stat__value">{trends.level}</span></div>
          <div className="stat"><span className="stat__label">{t('settings.avg_ticket')}</span><span className="stat__value">{formatMoney(trends.avg_ticket_cents ?? 0, currency, locale)}</span></div>
          <div className="stat"><span className="stat__label">{t('settings.avg_turnover')}</span><span className="stat__value">{trends.avg_turnover_min ?? 0}</span></div>
        </div>
      ))}

      {/* Pagamento direto */}
      <h2 className="section-title">{t('pay.config_title')}</h2>
      <p className="muted-note">{t('pay.config_hint')}</p>
      <form onSubmit={savePayment} className="form">
        {currency === 'BRL' && (
          <>
            <label className="field"><span>{t('pay.pix_key')}</span><input value={pixKey} onChange={(e) => setPixKey(e.target.value)} /></label>
            <div className="form-row">
              <label className="field"><span>{t('pay.pix_name')}</span><input value={pixName} onChange={(e) => setPixName(e.target.value)} maxLength={25} /></label>
              <label className="field"><span>{t('pay.pix_city')}</span><input value={pixCity} onChange={(e) => setPixCity(e.target.value)} maxLength={15} /></label>
            </div>
          </>
        )}
        {currency === 'EUR' && (
          <>
            <label className="field"><span>{t('pay.sepa_iban')}</span><input value={sepaIban} onChange={(e) => setSepaIban(e.target.value)} /></label>
            <label className="field"><span>{t('pay.sepa_name')}</span><input value={sepaName} onChange={(e) => setSepaName(e.target.value)} maxLength={70} /></label>
            <p className="muted-note">{t('pay.sepa_note')}</p>
          </>
        )}
        {currency !== 'BRL' && currency !== 'EUR' && <p className="muted-note">{t('pay.unsupported')}</p>}
        {(currency === 'BRL' || currency === 'EUR') && (
          <div className="form-row">
            <button type="submit" className="btn btn--primary">{t('common.save')}</button>
            {paySaved && <span className="form-info">{t('loyalty.saved')}</span>}
          </div>
        )}
      </form>

      {/* LGPD */}
      <h2 className="section-title">{t('settings.lgpd_title')}</h2>
      <div className="form-row">
        <button type="button" className="btn btn--ghost" onClick={() => void exportData()}>{t('settings.export')}</button>
        <button type="button" className="btn btn--ghost" onClick={() => void deleteData()}>{t('settings.delete')}</button>
      </div>
      {msg && <p className="form-info">{msg}</p>}
    </div>
  );
}
